import { clamp } from "./utils.ts";
import {
  hexToOklch,
  normalizeHexColor,
  oklchToHex,
} from "./color-convert.ts";
import {
  apcaContrast,
  formatOklch,
  oklchToUnclampedLinearSrgb,
  type Oklch,
} from "./lightness.ts";

export type ThemeColorMode = "light" | "dark";
export type ThemeColorRole = "accent" | "background" | "foreground";

/**
 * How far surfaces step apart, as a multiple of the shipped step.
 *
 * `1` is the shipped ladder. `0` converges every rung onto the page and lets
 * the hairlines carry the separation instead — not an exotic mode, but what
 * light mode has always done here, since a page starting at white has no
 * headroom to step up into. Above `1` the ladder opens up.
 *
 * A scale rather than a flat/ladder flag because the palettes that want this
 * are not two camps: measured against the 36 in the tweakcn registry, authored
 * dark ladders run from 0 to 32 L with a cluster BELOW ours and a tail well
 * above it. A flag would have to round every one of them to a lie.
 */
const DEPTH_MAX = 6;

/**
 * The room the recessed steps are measured from at minimum, in reference
 * lightness — the dark page the ladder was originally drawn against.
 *
 * Floors the rail's recessed step so a near-black seed gets the same
 * separation the design was drawn with, rather than a fraction of nothing.
 * Deliberately NOT retuned when the shipped page moved up to L 21.3: the
 * current page clears the floor on its own, and raising it would silently
 * deepen every near-black catalog half's rail.
 */
const RECESS_FLOOR = 18;

/**
 * The darkest ground the RUNGS are measured from. The page keeps its own value.
 *
 * Eight-bit sRGB has almost no codes near black: every OKLCH lightness under 6
 * renders as `#000000`, and L 12 is still only `#060606`. So a ladder built as
 * fractions of the distance from a black page to its ink renders as no ladder
 * at all — a pure-black seed (Vercel dark) painted `sunken`, `sidebar`,
 * `popover`, `muted` and `card` the identical black, which is the whole shell
 * and every card gone.
 *
 * The reference implementation never hits this because it mixes in 8-bit sRGB,
 * where the same proportion off black reaches `#0a0a0a`. Rather than change the
 * mixing space — OKLab is what keeps every other theme's steps even — the RUNGS
 * are measured from a ground no darker than this, while the page stays exactly
 * where the seed put it. A page darker than the shipped one therefore gets the
 * shipped ladder's spacing rather than a compressed copy of it.
 *
 * 16 — under the L 18 page the ladder was originally drawn against, and well
 * under the shipped L 21.3 page — so only grounds that genuinely cannot render
 * are touched; a seed at or above it resolves exactly as before.
 */
const LADDER_FLOOR = 16;

/** The dial position that leaves the shipped ladder and hairlines untouched. */
const CONTRAST_NEUTRAL = 50;

/**
 * How much of the ink every plane off the page wears, before its own step.
 *
 * A tinted scheme has to tint its SURFACES, not only its text, and the ladder
 * on its own cannot deliver that. A rung is a step in LIGHTNESS, and a step of
 * a few L toward a saturated ink carries almost none of that ink's colour — on
 * a yellow seed the whole dark ladder came out within a rounding step of
 * neutral, and `popover`, at its 1.8% rung, landed on exactly zero chroma
 * because a ground leaning blue and an ink leaning yellow cancel on the way
 * past each other.
 *
 * The reference implementation gets a visible cast for free by laying
 * `rgba(ink, α)` over the surface in eight-bit sRGB, where a small step off an
 * encoded near-max channel is a large step in colourfulness. The same step
 * measured in OKLab — the space that keeps our rungs evenly spaced — is not.
 * So the cast is its own axis.
 *
 * A floor rather than pure proportionality because the cast is a property of
 * BEING a distinct plane, not of how far that plane travelled: our navigation
 * planes step barely 1 L off the page and would otherwise stay neutral, and
 * the sidebar is the largest continuous surface in the shell.
 */
const CAST_FLOOR = 0.048;

/**
 * The ink a plane wears on top of the floor, per unit of lightness it steps.
 *
 * Both this and `CAST_FLOOR` are a fit to the reference implementation's four
 * dark planes, matched role for role against ours: its panel to our `sidebar`,
 * its two elevated rungs to our `popover` and `card`, its control fill to our
 * `muted`. It holds those within a rounding step, which is as close as one
 * rule gets to a ladder drawn twice as deep as ours.
 */
const CAST_PER_STEP = 0.039;

/**
 * The most ink a plane may wear, however far off the page it sits.
 *
 * Nothing on the shipped ladders comes near it — the deepest rung steps about
 * 6 L — so it binds only on a seed whose planes are pulled far apart, where it
 * is what keeps a surface a surface rather than a colour.
 */
const CAST_CEILING = 0.3;

/**
 * The ladder's travel, as a multiple of the shipped step.
 *
 * Asymmetric on purpose, and in the same direction the reference implementations
 * lean: below neutral the rungs converge gently, because a ladder that collapses
 * fast takes the whole layout with it, and the hairline boost below has to be
 * able to catch what the fills give up. Above neutral it opens faster, because
 * that end of the dial is asked for by someone who wants to SEE the structure.
 */
function separationScale(contrast: number | undefined): number {
  if (contrast == null) return 1;
  const travel = (clamp(contrast, 0, 100) - CONTRAST_NEUTRAL) / CONTRAST_NEUTRAL;
  return 1 + travel * (travel <= 0 ? 0.55 : 0.9);
}

/**
 * The hairline's own lift above neutral.
 *
 * One-directional: below neutral the fills converge and `hairlineBoost` already
 * takes the line over, so lifting here too would count the same compensation
 * twice. Above neutral the fills separate on their own and the line only has to
 * keep pace with them.
 */
function hairlineScale(contrast: number | undefined): number {
  if (contrast == null) return 1;
  return (
    1 + Math.max(0, (clamp(contrast, 0, 100) - CONTRAST_NEUTRAL) / CONTRAST_NEUTRAL) * 0.5
  );
}

export interface ThemeColorSeed {
  accent: string;
  background: string;
  foreground: string;
  /**
   * How far apart the surfaces sit and how loud the QUIET ink reads, 0-100.
   *
   * Body ink is not on this dial. `foreground` is the body, stated by the user
   * and painted verbatim, so no position of this control can make the text
   * anyone actually reads harder to see. What moves is everything around it:
   * the surface ladder, the secondary and faint rungs, the hairlines.
   *
   * Omitted means a seed authored before the dial existed. Those keep the
   * shipped ladder and take their quiet rungs from the stored foreground, so
   * every saved theme resolves exactly as it did until its dial is touched.
   */
  contrast?: number;
  /**
   * The hairline's own color, for a palette whose borders are part of its
   * character rather than a quiet edge derived from the fill. When set it is
   * used AS the hairline instead of a mix toward the foreground, so every
   * surface's edge reads the same deliberate line.
   */
  border?: string;
  /**
   * Ladder scale. Omitted means `1` — the shipped step — so every existing
   * seed resolves exactly as it did.
   */
  depth?: number;
  /**
   * Paint `primary` as the shipped NEUTRAL pair rather than from the accent.
   *
   * What "Default accent" means on the shipped design: a near-black button in
   * light, a white one in dark, with the accent hue showing only where it is
   * ink — `brand`, `ring`, the tinted chip. A custom theme with the accent left
   * on Default was getting a blue button instead, so the same choice produced
   * two different products depending on which palette was underneath it.
   */
  neutralPrimary?: boolean;
}

export interface ThemeColorSeeds {
  light: ThemeColorSeed | null;
  dark: ThemeColorSeed | null;
}

/**
 * Which way the dark navigation planes — `sidebar` and the rail — travel off
 * the page. UNSETTLED: three live arrangements under evaluation, which is why
 * this is a resolver OPTION and not a seed field. It names a viewer-session
 * preference about the shell, not a property of a palette, so it persists in
 * no stored theme and rides no preference round-trip.
 *
 * `recess` (the default, and what an absent option means) is the spine's
 * arrangement: the whole navigation ladder steps toward black, pane above
 * rail, both under the page, so the page is the brightest large plane and the
 * cards lift off it. `lift` steps the pane toward the ink — the reference
 * engine's panel direction — with the rail canvas recessed beneath the page,
 * splitting the navigation planes across it. `lift-flat` lifts the pane the
 * same way but paints the rail canvas AT the page, the reference shell's own
 * composition: one floating panel on an otherwise continuous surface, the
 * island reading by its border and drop alone. Every other rung resolves
 * identically under all three.
 */
export type DarkNavDirection = "lift" | "lift-flat" | "recess";

export interface CustomThemeOptions {
  darkNav?: DarkNavDirection;
}

/* eslint-disable no-restricted-syntax -- User-editable seed defaults must be
   literal source colors. Resolving them from semantic tokens would make the
   custom-theme input depend on its own output. */
export const DEFAULT_THEME_COLOR_SEEDS: Record<
  ThemeColorMode,
  ThemeColorSeed
> = {
  light: {
    accent: "#2563eb",
    background: "#fcfcfc",
    foreground: "#171717",
  },
  dark: {
    accent: "#1677d2",
    // The shipped dark page. The editor's "Default" background swatch reads
    // this — walking away from the product default must be one click home.
    background: "#191919",
    foreground: "#a9a9a9",
  },
};
/* eslint-enable no-restricted-syntax */

export const EMPTY_THEME_COLOR_SEEDS: ThemeColorSeeds = {
  light: null,
  dark: null,
};

const WHITE: Oklch = { l: 100, c: 0, h: 0 };
const BLACK: Oklch = { l: 0, c: 0, h: 0 };

export function resolveThemeColorSeed(value: unknown): ThemeColorSeed | null {
  if (typeof value !== "object" || value === null) return null;
  const candidate = value as Record<string, unknown>;
  const accent =
    typeof candidate.accent === "string"
      ? normalizeHexColor(candidate.accent)
      : null;
  const background =
    typeof candidate.background === "string"
      ? normalizeHexColor(candidate.background)
      : null;
  const foreground =
    typeof candidate.foreground === "string"
      ? normalizeHexColor(candidate.foreground)
      : null;
  if (!accent || !background || !foreground) return null;
  const seed: ThemeColorSeed = { accent, background, foreground };
  if (
    typeof candidate.contrast === "number" &&
    Number.isFinite(candidate.contrast)
  ) {
    seed.contrast = Math.round(clamp(candidate.contrast, 0, 100));
  }
  // Anything not exactly `true` reads as unset — the hue paints — so a seed
  // authored before the flag existed resolves exactly as it did.
  if (candidate.neutralPrimary === true) seed.neutralPrimary = true;
  return seed;
}

/**
 * The readable ink — white or near-black — for content sitting ON an
 * arbitrary color (a color-well trigger painted in its own value). Chosen by
 * APCA contrast, the same metric the theme solver targets.
 */
/* eslint-disable no-restricted-syntax -- The ink pair must be literal
   colors: it sits on an arbitrary USER-chosen fill, so no semantic token can
   name it, and it must not move when the theme flips. */
export function contrastInkFor(hex: string): string {
  const background = hexToOklch(hex);
  if (!background) return "#ffffff";
  const white = Math.abs(apcaContrast(WHITE, background));
  const black = Math.abs(apcaContrast(BLACK, background));
  return white >= black ? "#ffffff" : "#111111";
}
/* eslint-enable no-restricted-syntax */

// The contrast dial's travel, as interpolation positions between the
// background and its opposing pole (pure white or black). The floor keeps
// the softest setting legible; the ceiling stops short of the pole so the
// derived ink never reads as raw #fff/#000.
/**
 * The contrast dial's travel, as a multiplier on each role's authored target.
 *
 * The SAME range the generated presets run (`CONTRAST_MULTIPLIER`, least →
 * most), because that range is the one solved and asserted across fifty cells.
 * A dial that could go further would only be able to go somewhere unreadable.
 */
const CONTRAST_MULTIPLIER_MIN = 0.68;
const CONTRAST_MULTIPLIER_MAX = 1.24;

/**
 * What each ink reads at, in the shipped design, measured on its own page.
 *
 * These are TARGETS, not lightnesses, and that is the whole point: the dial
 * scales a contrast each role owes rather than sliding one lightness and
 * letting the rest ride along on fixed fractions. Body climbs steeply, the
 * quiet rungs barely move, and neither can be dragged under its floor —
 * which is the behaviour the preset axis has always had and the free dial
 * never did.
 */
const ROLE_TARGET: Record<ThemeColorMode, { body: number; secondary: number; faint: number }> = {
  light: { body: 103.3, secondary: 81, faint: 56.2 },
  dark: { body: 90.4, secondary: 62.1, faint: 30.2 },
};

/**
 * The least a rung may sit under the one above it and still read as quieter.
 *
 * Small on purpose: it only has to bind on a soft body, where the band between
 * the floor and the body is narrow to begin with.
 */
const QUIET_GAP = 4;

/**
 * The least a hairline may sit off the surface it edges, in L, before it
 * stops reading as a line. Lines are mixed as a fraction of the ink–surface
 * gap, so a soft-bodied seed produces lines within a step of their fill; the
 * floors catch those. The shipped design's quietest divider sits ~6 L off
 * its page and its input edge ~6 (light) / ~9 (dark), so neither floor moves
 * a shipped or catalog value. The input edge takes the higher floor because it
 * is the edge that says "type here" — a divider can be quiet, a field cannot
 * disappear.
 */
const BORDER_FLOOR_L = 2;
const INPUT_FLOOR_L = 4;

/** The point past which a role stops being usable. Mirrors the spine's floors. */
const ROLE_FLOOR = { body: 60, secondary: 45, faint: 30 } as const;

/**
 * The most contrast a ground can give, stopping short of the pole as the ink
 * does. A near-white page cannot reach the same numbers a mid one can.
 */
function contrastCeiling(background: Oklch): number {
  const pole = apcaContrast(WHITE, background) >= apcaContrast(BLACK, background)
    ? WHITE
    : BLACK;
  return Math.abs(
    apcaContrast(
      {
        l: background.l + (pole.l - background.l) * 0.97,
        c: background.c * 0.03,
        h: background.h,
      },
      background,
    ),
  );
}

/**
 * The dial's travel ON THIS GROUND.
 *
 * The top is whatever the background can actually deliver, never more. Fixing
 * the ceiling at a multiple of the AUTHORED target made the dial ask for
 * contrast a page could not produce: on `#fcfcfc` the top asked for Lc 128
 * against a ceiling of 104, so the last 41% of the travel changed nothing and
 * the thumb — positioned by reading the seed back — could not be dragged past
 * 59. Scaling to the ground means every position moves something and both ends
 * are reachable.
 */
function multiplierRange(
  mode: ThemeColorMode,
  background: Oklch,
): [number, number] {
  const authored = ROLE_TARGET[mode].body;
  // Less the margin the ink is solved WITH: the range describes what the dial
  // may ASK for, and the solve adds the margin on top. Without this the top of
  // the travel asks for a point more than the ground has and the thumb stops
  // short of its own end.
  const reachable = (contrastCeiling(background) - ROUNDING_MARGIN) / authored;
  return [
    CONTRAST_MULTIPLIER_MIN,
    Math.max(
      CONTRAST_MULTIPLIER_MIN + 0.01,
      Math.min(CONTRAST_MULTIPLIER_MAX, reachable),
    ),
  ];
}

const contrastMultiplier = (
  contrast: number,
  mode: ThemeColorMode,
  background: Oklch,
): number => {
  const [low, high] = multiplierRange(mode, background);
  return low + clamp(contrast / 100, 0, 1) * (high - low);
};

/**
 * The ink that reads at `target` on `background`, keeping the ground's hue.
 *
 * Bisects lightness toward the readable pole rather than interpolating to a
 * fixed position, so the answer is a measured contrast instead of a guess that
 * happens to look right on the seed it was tuned against. Chroma fades as the
 * ink travels, so a tinted scheme keeps a cast without the ink turning into a
 * colour of its own.
 */
/**
 * Solved a point ABOVE the asked-for target.
 *
 * A bisection converges ON its target and `formatOklch` then rounds the result,
 * which lands the emitted color a hair under the bar it was solved for. Third
 * place this bites, so it is a name rather than a literal.
 */
const ROUNDING_MARGIN = 1;

/**
 * Where the ink travels, and why a QUIET rung travels somewhere else.
 *
 * `toward` is the seed's own ink. Given one that carries the target, the
 * bisection runs along the mix from the ground to THAT colour, so a quiet rung
 * comes out as the same colour as the text it is subordinate to, only fainter —
 * the relationship `rgba(ink, α)` states outright in the reference
 * implementation. Walking to the pole instead fades chroma to nothing and
 * substitutes the GROUND's hue, which is why a themed interface used to paint
 * every secondary label, description and icon in neutral grey no matter what
 * ink it was seeded with.
 *
 * The pole is still the answer in two cases. `deriveForegroundSeed` is solving
 * FOR the ink and has none to walk toward, and a target above what the ink
 * itself reads has to keep climbing past it.
 *
 * A neutral ink over a neutral ground puts both paths on the same axis and the
 * bisection converges on the same lightness, so every shipped seed resolves
 * exactly as it did.
 */
function inkAtContrast(
  background: Oklch,
  asked: number,
  toward?: Oklch,
): Oklch {
  const target = asked + ROUNDING_MARGIN;
  const pole = apcaContrast(WHITE, background) >= apcaContrast(BLACK, background)
    ? WHITE
    : BLACK;
  const ink =
    toward && Math.abs(apcaContrast(toward, background)) >= target
      ? toward
      : null;
  const at = (t: number): Oklch =>
    ink
      ? mix(background, ink, t)
      : {
          l: background.l + (pole.l - background.l) * t,
          c: background.c * (1 - t),
          h: background.h,
        };
  // Stop just short of the pole so a maxed dial never lands on raw #fff/#000.
  // The ink is a stated colour and has no such edge: its own end of the travel
  // is a legitimate answer, and the guard above means the bisection never
  // needs it anyway.
  const ceiling = ink ? 1 : 0.97;
  // The far end is the most contrast available; if even that misses the target
  // there is nothing further to reach for.
  if (Math.abs(apcaContrast(at(1), background)) < target) return at(ceiling);
  let low = 0;
  let high = 1;
  for (let iteration = 0; iteration < 32; iteration += 1) {
    const mid = (low + high) / 2;
    if (Math.abs(apcaContrast(at(mid), background)) >= target) high = mid;
    else low = mid;
  }
  return at(Math.min(high, ceiling));
}

/** The target a role owes at a given dial position. */
function roleTarget(
  mode: ThemeColorMode,
  role: keyof typeof ROLE_FLOOR,
  contrast: number,
  background: Oklch,
): number {
  const authored = ROLE_TARGET[mode][role];
  const scaled = authored * contrastMultiplier(contrast, mode, background);
  // Clamped to the floor, and to the authored value when that already sits
  // below it — several roles ship quiet on purpose, and a floor that outranked
  // them would make the default louder than the design.
  return Math.max(scaled, Math.min(ROLE_FLOOR[role], authored));
}

/**
 * The body ink for a background at a 0–100 reading strength.
 *
 * The user never picks text directly — one drag on a text colour makes the
 * whole interface illegible — so this answers with an ink that MEASURES at the
 * body role's scaled target.
 *
 * NOT the contrast dial. The dial moves the surfaces and the quiet rungs
 * around a body that holds still; this is what keeps that body legible when
 * the GROUND moves under it, and what the catalog importer solves ink with.
 */
export function deriveForegroundSeed(
  background: string,
  contrast: number,
  mode: ThemeColorMode = "dark",
): string {
  const base = hexToOklch(background) ?? BLACK;
  return oklchToHex(
    inkAtContrast(base, roleTarget(mode, "body", contrast, base)),
  );
}

/**
 * The inverse: the 0–100 contrast whose derivation best reproduces a stored
 * foreground seed on this background. Positions the dial for themes saved
 * before (or outside) the contrast model without changing what they paint.
 */
export function foregroundSeedContrast(
  background: string,
  foreground: string,
  mode: ThemeColorMode = "dark",
): number {
  const base = hexToOklch(background) ?? BLACK;
  const ink = hexToOklch(foreground);
  if (!ink) return 50;
  // Measured, not geometric: read what the stored ink ACTUALLY reads at, then
  // ask which dial position would have asked for that. A seed saved under the
  // old lightness model, or imported from outside the model entirely, lands on
  // the position whose target it already meets.
  // Net out the margin the ink was solved WITH, or the thumb reports a hair
  // more contrast than the dial asked for and can never quite reach zero.
  const reads = Math.max(
    0,
    Math.abs(apcaContrast(ink, base)) - ROUNDING_MARGIN,
  );
  const authored = ROLE_TARGET[mode].body;
  // Inverted through the SAME ground-scaled range the dial travels, or the
  // thumb would sit at a position the dial can never be dragged to.
  const [low, high] = multiplierRange(mode, base);
  if (high <= low) return 50;
  const position = Math.round(
    clamp((reads / authored - low) / (high - low), 0, 1) * 100,
  );
  if (position === 0 || position === 100) return position;
  // The emitted ink is rounded to the 8-bit hex grid, and on some grounds that
  // rounding lands a derived POLE ink a fraction of a position inside the
  // travel — so a thumb dragged to an end could never read back AS the end.
  // An ink that IS the pole derivation reads as the pole, whatever the grid
  // did; every other position already carries the loop's own tolerance.
  if (foreground === deriveForegroundSeed(background, 0, mode)) return 0;
  if (foreground === deriveForegroundSeed(background, 100, mode)) return 100;
  return position;
}

/**
 * A background change under the contrast model: the held contrast is
 * re-derived on the new ground, so moving the floor moves the ink with it,
 * never under it. Every editor writes background through this so the two
 * hosts (theme popup, settings rows) cannot drift.
 */
export function seedWithBackground(
  seed: ThemeColorSeed,
  background: string,
  mode: ThemeColorMode = "dark",
): ThemeColorSeed {
  return {
    ...seed,
    background,
    foreground: deriveForegroundSeed(
      background,
      foregroundSeedContrast(seed.background, seed.foreground, mode),
      mode,
    ),
  };
}

/**
 * A contrast change. The dial is STORED, and the foreground is left alone.
 *
 * Two values rather than one: an ink that encoded the dial would make body
 * text the thing the dial moves. Storing the position separately is what lets
 * the body hold still while the surfaces and the quiet rungs travel.
 */
export function seedWithContrast(
  seed: ThemeColorSeed,
  contrast: number,
): ThemeColorSeed {
  return { ...seed, contrast: Math.round(clamp(contrast, 0, 100)) };
}

/**
 * The dial's position for a seed — the one source a control seeds itself from.
 *
 * A seed that states nothing sits at NEUTRAL: the shipped ladder, the position
 * where the scale is a no-op. Reading the fallback back off the ink would only
 * make sense if the ink WERE the dial; with the dial inverted — body holds,
 * surfaces and quiet rungs travel — it reports how loud a theme's text is
 * rather than how far apart its surfaces sit, and a high-contrast palette
 * opens at 96 with nothing about it set to 96.
 *
 * The reference application does the same thing for the same reason: its seed
 * builder emits no contrast at all, and a preset moves the dial only by
 * authoring one outright.
 */
export function seedContrast(seed: ThemeColorSeed): number {
  return seed.contrast ?? CONTRAST_NEUTRAL;
}

function toOklab(color: Oklch): [number, number, number] {
  const angle = (color.h * Math.PI) / 180;
  return [
    color.l,
    color.c * Math.cos(angle),
    color.c * Math.sin(angle),
  ];
}

function mix(first: Oklch, second: Oklch, amount: number): Oklch {
  const a = toOklab(first);
  const b = toOklab(second);
  const weight = clamp(amount, 0, 1);
  const lightness = a[0] + (b[0] - a[0]) * weight;
  const axisA = a[1] + (b[1] - a[1]) * weight;
  const axisB = a[2] + (b[2] - a[2]) * weight;
  const chroma = Math.sqrt(axisA * axisA + axisB * axisB);
  return {
    l: lightness,
    c: chroma,
    h:
      chroma < 0.0001
        ? 0
        : ((Math.atan2(axisB, axisA) * 180) / Math.PI + 360) % 360,
  };
}

function linearDisplayP3(color: Oklch): [number, number, number] {
  const [red, green, blue] = oklchToUnclampedLinearSrgb(color);
  return [
    0.82259287 * red + 0.17753395 * green,
    0.03319951 * red + 0.9667835 * green,
    0.01708535 * red + 0.07239572 * green + 0.91030148 * blue,
  ];
}

function fitsDisplayP3(color: Oklch): boolean {
  return linearDisplayP3(color).every(
    (channel) => channel >= -0.000001 && channel <= 1.000001,
  );
}

/** Keep requested chroma until the target lightness reaches Display P3's edge. */
function fitChromaToDisplayP3(color: Oklch): Oklch {
  if (fitsDisplayP3(color)) return color;
  let lower = 0;
  let upper = color.c;
  for (let iteration = 0; iteration < 40; iteration += 1) {
    const chroma = (lower + upper) / 2;
    if (fitsDisplayP3({ ...color, c: chroma })) lower = chroma;
    else upper = chroma;
  }
  return { ...color, c: lower };
}

function solidAccentForWhiteText(accent: Oklch): Oklch {
  if (apcaContrast(WHITE, accent) >= 60) return accent;
  let lower = 0;
  let upper = accent.l;
  for (let iteration = 0; iteration < 48; iteration += 1) {
    const lightness = (lower + upper) / 2;
    const candidate = fitChromaToDisplayP3({ ...accent, l: lightness });
    if (apcaContrast(WHITE, candidate) >= 60) lower = lightness;
    else upper = lightness;
  }
  return fitChromaToDisplayP3({ ...accent, l: lower });
}

/**
 * The accent, moved until it reads as INK on a given fill.
 *
 * The target is a parameter because the same hue serves two jobs at two bars:
 * as a quiet mark on the page it only has to be seen (45), but as the label of
 * a tinted chip it is body text and owes the body floor (60). Solving both
 * against the page is what left the chip's own pair unchecked.
 */
function accentInkForSurface(
  mode: ThemeColorMode,
  accent: Oklch,
  background: Oklch,
  target = 45,
): Oklch {
  if (apcaContrast(accent, background) >= target) return accent;
  // A moved accent asks for the gamut's chroma, not the seed's. Lightness
  // alone reads as a pastel of the hue — the same chroma looks thinner the
  // lighter it sits — so the lift carries the chroma out to the display's
  // edge and `fitChromaToDisplayP3` clamps it there. A near-neutral accent
  // keeps its own chroma: it has no hue to make vivid.
  const chroma = vividChroma(accent);
  const brighten = mode === "dark";
  let passing = brighten ? 100 : 0;
  let failing = accent.l;
  for (let iteration = 0; iteration < 48; iteration += 1) {
    const lightness = (passing + failing) / 2;
    const candidate = fitChromaToDisplayP3({ ...accent, c: chroma, l: lightness });
    if (apcaContrast(candidate, background) >= target) passing = lightness;
    else failing = lightness;
  }
  return fitChromaToDisplayP3({ ...accent, c: chroma, l: passing });
}

/** Below this chroma an accent is a neutral, and vividness has no hue to
 *  act on. */
const CHROMATIC_FLOOR = 0.04;

/** The chroma a moved accent reaches for before the gamut clamps it. Above
 *  every sRGB blue's edge, so the clamp — not this number — sets the value. */
const VIVID_CHROMA = 0.2;

function vividChroma(accent: Oklch): number {
  return accent.c < CHROMATIC_FLOOR
    ? accent.c
    : Math.max(accent.c, VIVID_CHROMA);
}

/**
 * The chroma the accent WASH holds, per mode. A tinted fill is the accent
 * hue at the surface's depth: mixing the accent INTO the grey surface
 * instead loses chroma in proportion to the surface's share, and a 20% mix
 * reads as grey with a memory of blue. Dark can hold more — a deep fill
 * carries chroma a pale one cannot without leaving the gamut.
 */
const TINT_CHROMA: Record<ThemeColorMode, number> = {
  light: 0.04,
  dark: 0.1,
};

function veil(color: Oklch, alpha: number): string {
  return formatOklch(color, alpha);
}

/**
 * The `background` surface's drop — the outer content island's shadow — derived
 * from the seed instead of frozen at a fixed black. A shadow is occlusion, so
 * it always darkens: it derives from the theme BACKGROUND pushed toward black
 * (never the foreground, which is light in dark mode and could not cast a
 * shadow) and keeps a whisper of the ground's own hue so a tinted scheme gets a
 * matching cast. Its strength tracks the theme's ink-to-ground contrast — a
 * soft, low-contrast scheme gets a softer drop.
 *
 * The geometry is one tight contact drop shared by both modes — no diffuse
 * halo, no spread ring. Only the alpha rises for the dark ground, so flipping
 * appearance changes the drop's weight, never its shape, and no mode paints
 * the hard dark outline a ring does the moment a themed gutter turns lighter
 * than pure black. It matches the authored `--background-shadow` token, so
 * the default seed lands on baseline.
 */
function backgroundShadow(
  mode: ThemeColorMode,
  background: Oklch,
  foreground: Oklch,
): string {
  const contrast = clamp(Math.abs(apcaContrast(foreground, background)) / 100, 0, 1);
  const intensity = 0.6 + 0.4 * contrast;
  const alpha = (base: number): number =>
    Number((base * intensity).toFixed(4));
  // Dark mode is a near-pure black occlusion; light mode keeps a trace of the
  // ground's hue by stopping just short of black.
  const pole = mix(background, BLACK, mode === "dark" ? 1 : 0.9);
  const contact = mode === "dark" ? 0.2 : 0.055;
  return `0 1px 2px 0 ${formatOklch(pole, alpha(contact))}`;
}

const CUSTOM_THEME_TOKENS = [
  "background",
  "background-veil",
  "background-shadow",
  // The scope's reset plumbing. A custom theme has to state these too, or
  // `[data-on="background"]` inside one resolves them from the GENERATED
  // defaults and the region leaks the shipped palette.
  "page-foreground",
  "page-border",
  "page-border-strong",
  "sunken",
  "sunken-veil",
  "card",
  "card-veil",
  "elevated",
  "elevated-veil",
  "popover",
  "popover-veil",
  "sidebar",
  "sidebar-veil",
  "sidebar-rail",
  "muted",
  "muted-veil",
  "secondary",
  "accent",
  "foreground",
  "card-foreground",
  "popover-foreground",
  "sidebar-foreground",
  "secondary-foreground",
  "accent-foreground",
  "muted-foreground",
  "faint-foreground",
  "border",
  "border-strong",
  "input",
  "sunken-border",
  "sunken-border-strong",
  "card-border",
  "card-border-strong",
  "elevated-border",
  "elevated-border-strong",
  "popover-border",
  "popover-border-strong",
  "sidebar-border",
  "sidebar-border-strong",
  "primary",
  "primary-foreground",
  "primary-hover",
  "primary-subtle",
  "primary-subtle-foreground",
  "brand",
  "ring",
  "wash-hover",
  "wash-selected",
  "wash-active",
  "wash-selected-hover",
  "wash-skeleton",
] as const;

export type CustomThemeToken = (typeof CUSTOM_THEME_TOKENS)[number];
export type CustomThemeProperties = Record<CustomThemeToken, string>;

/**
 * Values a palette states outright instead of having solved.
 *
 * Only tokens that ALREADY exist — this adds no custom property to the system,
 * it changes where a handful of them get their value. A surface named here is
 * substituted into the ramp BEFORE anything derives from it, so its veil and
 * its hairlines follow the stated fill rather than the solved one they would
 * otherwise still be carrying. Leaves are written at the end, having nothing
 * downstream of them.
 *
 * Every entry is validated against the same APCA floors the solver targets
 * before it reaches the catalog; one that fails is simply not recorded, and the
 * solved value stands. See `scripts` in the theme-catalog notes.
 */
export interface ThemeOverrides {
  /** Ramp rungs. Substituted before veils and hairlines derive. */
  muted?: string;
  popover?: string;
  sidebar?: string;
  /** Leaves: nothing derives from these. */
  "muted-foreground"?: string;
  input?: string;
  ring?: string;
  "sidebar-border"?: string;
}

export function resolveCustomTheme(
  mode: ThemeColorMode,
  seed: ThemeColorSeed,
  overrides?: ThemeOverrides,
  options?: CustomThemeOptions,
): CustomThemeProperties | null {
  const accent = hexToOklch(seed.accent);
  const background = hexToOklch(seed.background);
  const foreground = hexToOklch(seed.foreground);
  if (!accent || !background || !foreground) return null;
  // Lifting is opt-in: an unset option resolves to the spine's own
  // arrangement, the whole navigation ladder under the page. Both lifted
  // variants raise the pane the same way; they differ only in where the rail
  // canvas sits. See {@link DarkNavDirection}.
  const navLift =
    options?.darkNav === "lift" || options?.darkNav === "lift-flat";

  // Scaling the ladder keeps its ORDER at every setting: each rung is the same
  // fraction of a smaller or larger travel, so `sunken` stays under the page
  // and `card` stays over it and nothing downstream needs a special case.
  //
  // Two things multiply into one step. `depth` is the PALETTE's character —
  // how far apart its author drew the planes — and stays dark-only, because
  // light mode separates `card` by mixing toward WHITE rather than toward the
  // ink and scaling that measurably worsened fidelity against the reference
  // palettes. The dial is the USER's, and applies to both modes: light has
  // travel too, just downward, since a page already at the white wall separates
  // by recessing `sunken` and `sidebar` under it rather than lifting anything
  // over it.
  /**
   * A plane's lightness, wearing the ink's cast at `CAST_PER_STEP`.
   *
   * Chroma is REBUILT from the page rather than added to whatever the plane
   * arrived with, which makes this idempotent — the rail is a step off a
   * sidebar that already wore its own cast, and a cast measured from the page
   * cannot compound down a chain. It is also what keeps two opposing hues from
   * cancelling: the ink is laid over the page's own chroma, never interpolated
   * through it.
   *
   * A neutral ink casts nothing and the plane comes back exactly as solved, so
   * both shipped seeds and every neutral palette resolve as they did. Nor does
   * a plane that never left the page — `sunken` collapses onto it on a black
   * ground — because a plane sharing the page's lightness and wearing a
   * different cast would read as a second page rather than as a rung.
   */
  const cast = (surface: Oklch): Oklch => {
    const step = Math.abs(surface.l - background.l);
    if (foreground.c < 0.0001 || step < 0.0001) return surface;
    const amount = Math.min(
      CAST_CEILING,
      CAST_FLOOR + step * CAST_PER_STEP,
    );
    const [, pageA, pageB] = toOklab(background);
    const [, inkA, inkB] = toOklab(foreground);
    const axisA = pageA + inkA * amount;
    const axisB = pageB + inkB * amount;
    const chroma = Math.sqrt(axisA * axisA + axisB * axisB);
    return {
      l: surface.l,
      c: chroma,
      h:
        chroma < 0.0001
          ? 0
          : ((Math.atan2(axisB, axisA) * 180) / Math.PI + 360) % 360,
    };
  };
  /**
   * A plane stepped DOWN from its base — the dark rail's rung, and the pane's
   * too under the `recess` arrangement. The step is floored at the room a
   * shipped dark page has, so a near-black ground gets a real step rather
   * than an imperceptible fraction of nothing; where even the floored ground
   * cannot go down, the step goes UP instead — a navigation plane owes the
   * shell a visible region boundary either way round, and on a black page
   * "deepest" is unsatisfiable while "distinct" is not.
   */
  const recessed = (base: Oklch, fraction: number): Oklch => {
    const drop = fraction * Math.max(base.l, RECESS_FLOOR);
    if (base.l >= drop) return cast(mix(base, BLACK, fraction));
    const reach = Math.max(foreground.l - base.l, 0.0001);
    return cast(mix(base, foreground, Math.min(1, drop / reach)));
  };
  /**
   * A surface lifted to somewhere a step off it can actually be seen.
   *
   * The same eight-bit problem the ladder has, and it bites hairlines harder:
   * a page at pure black gave `--border` at L 9.5, which renders `#030303`, so
   * the content island had no edge and no drop either — a shadow is occlusion
   * and cannot lighten, so on a black page the LINE is the only thing left to
   * carry the island. It washed out completely.
   */
  const renderable = (surface: Oklch): Oklch =>
    mode === "dark" && surface.l < LADDER_FLOOR
      ? { ...surface, l: LADDER_FLOOR }
      : surface;
  /** A plane stepped `fraction` of the way toward the ink, wearing its cast. */
  const towardInk = (base: Oklch, fraction: number): Oklch =>
    cast(mix(base, foreground, fraction));
  // The rungs measure from here; the PAGE keeps `background` untouched.
  const ground = renderable(background);
  const separation = separationScale(seed.contrast);
  const step =
    (mode === "dark" ? clamp(seed.depth ?? 1, 0, DEPTH_MAX) : 1) * separation;
  // A stated rung replaces the solved one HERE, ahead of the veils and the
  // hairlines below, which read from this object — so a palette that names its
  // popover gets a popover veil and popover hairlines to match, instead of the
  // solved ones it would otherwise still be wearing.
  const stated = (name: keyof ThemeOverrides): Oklch | null => {
    const value = overrides?.[name];
    return value ? hexToOklch(value) : null;
  };
  // The rail strip — the with-page shell canvas — is the DEEPEST plane in
  // both modes, so the island reads as resting ON the shell however the pane
  // travels. Dark splits the navigation planes across the page on purpose:
  // the pane lifts toward the ink (the reference panel) while the canvas
  // recesses beneath the page and grounds it — a canvas lighter than the
  // pane beside it read as the shell floating over its own navigation. It
  // takes the depth scale like every other rung.
  const solvedSurfaces =
    mode === "dark"
      ? {
          // The ONE dark rung that steps toward black, measured from the PAGE
          // rather than the floored ground: a well is defined by being cut
          // INTO the page, and one floating above it is not a shallower well,
          // it is a card. On a black page it collapses onto the page and the
          // hairline carries it — exactly what the reference implementation's
          // own recessed backdrop does on the same seed.
          sunken: cast(mix(background, BLACK, 0.14 * step)),
          background,
          // Under `lift`, the navigation planes step TOWARD THE INK, the
          // direction the reference engine's panel takes: it lays
          // `rgba(ink, 4.7%)` over the surface in 8-bit sRGB, and 0.06 in
          // OKLab reproduces that overlay within a rounding step on every
          // dark catalog ground — the Linear seed lands on the reference
          // app's measured panel exactly. Under `recess`, the spine's own
          // arrangement: the pane steps toward black like the rail beneath
          // it, `recessed` lifting only where a near-black page leaves no
          // room below. See {@link DarkNavDirection}.
          sidebar: navLift
            ? towardInk(ground, 0.06 * step)
            : recessed(ground, 0.07 * step),
          // Floating means BRIGHTEST large plane in dark — the reference
          // ground's own move: its menus lift decisively past its raised
          // planes rather than resting a hair off the page. A popover under
          // the card rung read as a cutout the moment a menu opened over a
          // card or a kanban column, with only the shadow arguing otherwise.
          // Just past `card` (0.05), short of the hover/selected fill (0.07)
          // so a menu never matches the state wash painted inside it.
          popover: towardInk(ground, 0.0575 * step),
          muted: towardInk(ground, 0.032 * step),
          card: towardInk(ground, 0.05 * step),
          // A card LIFTS off the page in dark, the direction light comes from.
          // Recessing it under the board instead — which is what riding the
          // navigation plane did — reads as a hole cut in the board rather
          // than an object resting on it, and it inverts against the shell:
          // the nav travels DOWN from the page, so a card travelling down too
          // leaves the board as the brightest plane on screen.
          //
          // It lifts onto the `muted` rung rather than `card`'s, which puts a
          // widget card on the same plane a kanban column already paints —
          // one substrate for the two board-shaped surfaces. Light has no
          // such pairing to make: there `muted` travels DOWN from the white
          // wall while the card planes hold at it, so `elevated` stays
          // twinned with `card` and only dark splits them.
          elevated: towardInk(ground, 0.032 * step),
          secondary: towardInk(ground, 0.05 * step),
          accent: towardInk(ground, 0.07 * step),
        }
      : {
          // The fractions ARE the shipped light ramp, measured off it: the
          // authored design puts the interaction fills nearest the page and the
          // navigation planes below them. Inverting that pair — muted deepest,
          // sidebar barely off the page — is the failure this guards against.
          // Matching means the shipped design is reproducible from its own
          // seed, which is what lets one solver answer for every theme.
          accent: towardInk(background, 0.017 * step),
          secondary: towardInk(background, 0.017 * step),
          muted: towardInk(background, 0.024 * step),
          sidebar: towardInk(background, 0.0355 * step),
          sunken: towardInk(background, 0.042 * step),
          background,
          // The lifted planes hold. A light page sits against the white wall
          // with nothing above it to climb into, so the dial moves what CAN
          // move — the recessed rungs above — and these stay where the design
          // put them.
          card: cast(mix(background, WHITE, 0.5)),
          elevated: cast(mix(background, WHITE, 0.5)),
          popover: cast(mix(background, WHITE, 0.5)),
        };
  const resolvedSidebar = stated("sidebar") ?? solvedSurfaces.sidebar;
  const surfaces = {
    ...solvedSurfaces,
    muted: stated("muted") ?? solvedSurfaces.muted,
    popover: stated("popover") ?? solvedSurfaces.popover,
    sidebar: resolvedSidebar,
    // The rail has no reference counterpart — that shell has a single panel —
    // so its step is ours, and it is the one plane the three dark
    // arrangements disagree about. `lift-flat` paints it AT the page — the
    // seed verbatim, no floor, no cast — so the gutters dissolve and the
    // island reads by its border and drop alone, which is the reference
    // shell's own composition. `lift` drops it one clear step under the
    // island's deepest fill: the ground, or a STATED pane that itself sits
    // under the page — derived from the ground alone, such a pane would land
    // beneath its own rail and the shell would invert; the solved lifted pane
    // never trips that guard. `recess` measures down from the pane,
    // compounding to the spine's 0.15 total when the pane is solved. Light
    // measures from the pane as its ladder always has; the fractions
    // reproduce the solved spine exactly, so nothing moves for a palette
    // that states nothing.
    "sidebar-rail":
      mode === "dark"
        ? options?.darkNav === "lift-flat"
          ? background
          : navLift
            ? recessed(
                resolvedSidebar.l < ground.l ? resolvedSidebar : ground,
                (resolvedSidebar.l < ground.l ? 0.08 : 0.15) * step,
              )
            : recessed(resolvedSidebar, (0.15 * step) / (1 - 0.07 * step))
        : towardInk(
            resolvedSidebar,
            (0.0718 - 0.0355) * step / (1 - 0.0355 * step),
          ),
  };
  // A shallower ladder gave up the lightness that separated its surfaces, so
  // the hairlines take over the job — the same trade the generated ramp makes
  // as its presets converge. Continuous with `step`, so nothing jumps at a
  // threshold, and a ladder at or above the shipped one gets no boost at all.
  const hairlineBoost =
    (1 + 0.8 * (1 - Math.min(step, 1))) * hairlineScale(seed.contrast);
  // The dark fractions are set against the PRODUCTS the catalog presets take
  // their colours from, not against the reference engine that ships them. That
  // engine draws its hairline at `alpha(ink, 8.4%)`, which is 0.10 of the
  // ink–ground gap here — and its Notion preset lands byte-exact on Notion's
  // page with the line a third quieter than Notion's own divider, its GitHub
  // preset half as loud as GitHub's `border-default`. A chat window can carry
  // that; a table grid is nothing but its hairlines. At 0.14 the Notion preset
  // meets Notion's divider (+9 L), the shipped seed sits with Linear and shadcn
  // (+10), and the strong rung reaches the band GitHub and Linear draw their
  // dividers in (+15–17).
  const borderAmount = (mode === "dark" ? 0.14 : 0.07) * hairlineBoost;
  const strongBorderAmount = (mode === "dark" ? 0.24 : 0.15) * hairlineBoost;
  const inputAmount = (mode === "dark" ? 0.19 : 0.07) * hairlineBoost;
  // A palette may name its hairline outright. Then it IS the line — the same
  // one on every surface, because a deliberate border is a deliberate border
  // wherever it lands — and the `-strong` rung leans it further off the fill
  // rather than mixing a fresh one. With no seed the line stays relative: a
  // quiet step off whatever fill it sits on, which is what keeps a hairline
  // visible as its surface moves through the preset matrix.
  const hairlineSeed = seed.border ? hexToOklch(seed.border) : null;
  /**
   * A hairline pulled back off the ink.
   *
   * `--input` is a hairline, but several controls paint a FILL from it and put
   * a label on that fill (the segmented thumb, a pressed outline toggle). A
   * palette whose stated border is the ink itself — a white brutalist edge on a
   * black page — then paints white on white. A line does not need to reach the
   * ink to read as a line, so this pulls it back toward its own surface until a
   * label could survive on it. Themes whose hairline is already quiet, the
   * shipped design included, are untouched.
   */
  const asFillSafeLine = (line: Oklch, surface: Oklch): Oklch => {
    // 61 for the same reason the chip's ink uses it: the bisection converges ON
    // its target and `formatOklch` then rounds the result under.
    if (Math.abs(apcaContrast(foreground, line)) >= 61) return line;
    // The pullback walks the line toward its surface until a label reads on
    // it. A seed whose ink cannot reach that bar against the SURFACE ITSELF —
    // a soft grey body on a grey page — has no such point, and the walk would
    // arrive at the surface: an input with no edge at all. Fill-safety is
    // unachievable for that palette wherever the line lands, so the line stays
    // a line.
    if (Math.abs(apcaContrast(foreground, surface)) < 61) return line;
    let safe = surface;
    let unsafe = line.l;
    for (let iteration = 0; iteration < 32; iteration += 1) {
      const candidate = { ...line, l: (safe.l + unsafe) / 2 };
      if (Math.abs(apcaContrast(foreground, candidate)) >= 61) safe = candidate;
      else unsafe = candidate.l;
    }
    return safe;
  };
  /**
   * A line held at least `floor` L off the surface it sits on, in the ink's
   * direction. The hairlines are a FRACTION of the ink–surface gap, so a seed
   * with a soft body (a grey ink on a grey page) mixes lines that land within a
   * lightness step of their fill and vanish — a control with no visible edge.
   * The floor is the least separation a line needs to read as a line; a seed
   * whose ink itself sits inside the floor gets the ink, the most a line can
   * ever move. Every shipped and catalog line already clears it, so this only
   * ever touches a palette that had lost its edges.
   */
  const asVisibleLine = (line: Oklch, surface: Oklch, floor: number): Oklch => {
    if (Math.abs(line.l - surface.l) >= floor) return line;
    const towardInk = Math.sign(foreground.l - surface.l) || 1;
    const reach = Math.min(floor, Math.abs(foreground.l - surface.l));
    return { ...line, l: clamp(surface.l + towardInk * reach, 0, 100) };
  };
  const border = (surface: Oklch, amount: number) =>
    formatOklch(
      asVisibleLine(
        hairlineSeed
          ? mix(hairlineSeed, foreground, Math.max(0, amount - borderAmount))
          : mix(renderable(surface), foreground, amount),
        renderable(surface),
        BORDER_FLOOR_L,
      ),
    );
  // The quiet rungs are SOLVED to their own targets, not mixed at a fixed
  // fraction of the body ink. A fraction makes every role move in lockstep, so
  // the dial scaled body, secondary and faint by the same factor and the whole
  // ramp went muddy together. The preset axis has always scaled each role's
  // target separately — body climbs steeply, faint barely moves — and this is
  // that behaviour on the free dial.
  //
  // The stored dial, or — for a seed authored before there was one — the
  // position its ink implies, so a pre-dial theme keeps the rungs it had.
  const dial = seedContrast(seed);
  // A quiet rung is quiet RELATIVE to the body beside it, and the targets alone
  // do not know that. A seed may author a deliberately soft body — the Notion
  // preset reads at Lc 82, under the 90 the ramp is drawn for — and
  // the dial climbs the quiet rungs on their own curves, so past the middle of
  // the travel `muted-foreground` overtook the text it is subordinate to and
  // the hierarchy read backwards. The ORDER outranks the target: each rung
  // stops short of the one above it, and the floors still apply underneath.
  const bodyReads = Math.abs(apcaContrast(foreground, background));
  const secondaryTarget = Math.min(
    roleTarget(mode, "secondary", dial, background),
    bodyReads - QUIET_GAP,
  );
  const faintTarget = Math.min(
    roleTarget(mode, "faint", dial, background),
    secondaryTarget - QUIET_GAP,
  );
  const mutedForeground = inkAtContrast(background, secondaryTarget, foreground);
  const faintForeground = inkAtContrast(background, faintTarget, foreground);
  // The shipped neutral pair, verbatim — the same numbers `tokens.css` carries
  // for `--primary` and `--primary-hover` in each mode. Stated rather than
  // derived because it IS the source value: reading it back out of a token
  // would make the resolver's input depend on its own output.
  const NEUTRAL: Record<ThemeColorMode, { fill: Oklch; hover: Oklch }> = {
    light: { fill: { l: 20.5, c: 0, h: 0 }, hover: { l: 14.5, c: 0, h: 0 } },
    dark: { fill: { l: 100, c: 0, h: 0 }, hover: { l: 92, c: 0, h: 0 } },
  };
  const primary = seed.neutralPrimary
    ? NEUTRAL[mode].fill
    : solidAccentForWhiteText(accent);
  const brand = accentInkForSurface(mode, accent, background);
  const primaryHover = seed.neutralPrimary
    ? NEUTRAL[mode].hover
    : fitChromaToDisplayP3({ ...primary, l: Math.max(0, primary.l - 5) });
  // The mix sets the wash's DEPTH — the step above the accent surface that
  // the shipped ramp measured — and the tint sets its color: the brand hue
  // at that depth, holding the mode's chroma. A neutral accent has no hue
  // to hold, so its wash stays the mix.
  const subtleDepth = mix(surfaces.accent, brand, mode === "dark" ? 0.2 : 0.12);
  const primarySubtle =
    brand.c < CHROMATIC_FLOOR
      ? subtleDepth
      : fitChromaToDisplayP3({
          c: Math.max(subtleDepth.c, TINT_CHROMA[mode]),
          h: brand.h,
          l: subtleDepth.l,
        });
  // Solved against the CHIP, not the page. `brand` clears the page at 45, but
  // the chip is a tinted fill the brand hue was just mixed INTO, so reusing it
  // here put the label and its own background within a few Lc of each other on
  // nearly every palette.
  // 61, not 60: the bisection converges ON its target, and `formatOklch` then
  // rounds the result — which lands the EMITTED color a hair under the floor it
  // was solved for. A point of margin is what makes the shipped value clear the
  // bar rather than balance on it.
  const primarySubtleInk = accentInkForSurface(mode, accent, primarySubtle, 61);

  return {
    background: formatOklch(surfaces.background),
    "background-veil": veil(surfaces.background, 0.8),
    "background-shadow": backgroundShadow(mode, surfaces.background, foreground),
    "page-foreground": formatOklch(foreground),
    "page-border": border(surfaces.background, borderAmount),
    "page-border-strong": border(surfaces.background, strongBorderAmount),
    sunken: formatOklch(surfaces.sunken),
    "sunken-veil": veil(surfaces.sunken, 0.8),
    card: formatOklch(surfaces.card),
    "card-veil": veil(surfaces.card, 0.8),
    elevated: formatOklch(surfaces.elevated),
    "elevated-veil": veil(surfaces.elevated, 0.8),
    popover: formatOklch(surfaces.popover),
    "popover-veil": veil(surfaces.popover, 0.8),
    sidebar: formatOklch(surfaces.sidebar),
    "sidebar-veil": veil(surfaces.sidebar, 0.8),
    "sidebar-rail": formatOklch(surfaces["sidebar-rail"]),
    muted: formatOklch(surfaces.muted),
    "muted-veil": veil(surfaces.muted, 0.9),
    secondary: formatOklch(surfaces.secondary),
    accent: formatOklch(surfaces.accent),
    foreground: formatOklch(foreground),
    "card-foreground": formatOklch(foreground),
    "popover-foreground": formatOklch(foreground),
    "sidebar-foreground": formatOklch(foreground),
    "secondary-foreground": formatOklch(foreground),
    "accent-foreground": formatOklch(foreground),
    "muted-foreground": formatOklch(stated("muted-foreground") ?? mutedForeground),
    "faint-foreground": formatOklch(faintForeground),
    border: border(surfaces.background, borderAmount),
    "border-strong": border(surfaces.background, strongBorderAmount),
    // The edge of a control asks for more than a divider does — it is what
    // says "type here" — so its floor is the higher one. The floor lifts a
    // vanished edge FIRST and fill-safety has the last word: on a quiet seed
    // whose ink sits close to the page, a line pushed the full 4 L would put
    // the label's fill inside the ink's reach, so the pullback holds the line
    // at the widest separation a label still reads on. A brutalist white edge
    // still comes off the ink (the floor never touches a loud line), and a
    // soft grey palette — where fill-safety is unachievable against the page
    // itself — keeps the floored edge untouched.
    input: formatOklch(
      asFillSafeLine(
        asVisibleLine(
          stated("input") ??
            // The same value `border()` would produce for this surface — the
            // input edge leans a touch off the plain hairline either way.
            (hairlineSeed
              ? mix(hairlineSeed, foreground, Math.max(0, inputAmount - borderAmount))
              : mix(renderable(surfaces.background), foreground, inputAmount)),
          renderable(surfaces.background),
          INPUT_FLOOR_L,
        ),
        surfaces.background,
      ),
    ),
    "sunken-border": border(surfaces.sunken, borderAmount),
    "sunken-border-strong": border(surfaces.sunken, strongBorderAmount),
    "card-border": border(surfaces.card, borderAmount),
    "card-border-strong": border(surfaces.card, strongBorderAmount),
    // Light `elevated` sits against the white wall with nowhere left to climb:
    // it lands within half a lightness point of the page, so the hairline IS
    // the edge and takes the LOUDEST line in the system rather than the
    // softest — which is what the 0.72 it was scaled by got backwards. Dark
    // has room above the page and lifts the plane instead, so its fill does
    // the separating and the line stays ordinary. A louder dark line would
    // also have the app-shell slab rule to clear: a dark hairline more than
    // 8L off its own fill reads as a glowing edge.
    "elevated-border": border(
      surfaces.elevated,
      borderAmount * (mode === "dark" ? 1 : 1.7),
    ),
    // The arrange cue has to clear whatever line rests underneath it, so it
    // follows the same split.
    "elevated-border-strong": border(
      surfaces.elevated,
      strongBorderAmount * (mode === "dark" ? 1 : 1.3),
    ),
    "popover-border": border(surfaces.popover, borderAmount),
    "popover-border-strong": border(surfaces.popover, strongBorderAmount),
    "sidebar-border": stated("sidebar-border")
      ? formatOklch(stated("sidebar-border")!)
      : border(surfaces.sidebar, borderAmount),
    "sidebar-border-strong": border(surfaces.sidebar, strongBorderAmount),
    primary: formatOklch(primary),
    // White on a solved accent, which `solidAccentForWhiteText` guarantees —
    // but the neutral pair flips in dark, where the fill IS white.
    "primary-foreground": formatOklch(
      seed.neutralPrimary
        ? (apcaContrast(BLACK, primary) >= apcaContrast(WHITE, primary)
            ? BLACK
            : WHITE)
        : WHITE,
    ),
    "primary-hover": formatOklch(primaryHover),
    "primary-subtle": formatOklch(primarySubtle),
    "primary-subtle-foreground": formatOklch(primarySubtleInk),
    brand: formatOklch(brand),
    ring: formatOklch(stated("ring") ?? brand),
    "wash-hover": veil(foreground, 0.05),
    "wash-selected": veil(foreground, mode === "dark" ? 0.07 : 0.08),
    "wash-active": veil(foreground, mode === "dark" ? 0.08 : 0.09),
    "wash-selected-hover": veil(
      foreground,
      mode === "dark" ? 0.1 : 0.11,
    ),
    "wash-skeleton": veil(foreground, mode === "dark" ? 0.07 : 0.05),
  };
}

export function applyCustomTheme(
  target: HTMLElement,
  mode: ThemeColorMode,
  seed: ThemeColorSeed | null,
  overrides?: ThemeOverrides,
  options?: CustomThemeOptions,
): void {
  const resolved = seed
    ? resolveCustomTheme(mode, seed, overrides, options)
    : null;
  for (const token of CUSTOM_THEME_TOKENS) {
    if (resolved) target.style.setProperty(`--${token}`, resolved[token]);
    else target.style.removeProperty(`--${token}`);
  }
  // `color-scheme` is the one thing a token cannot carry, and it is what the
  // BROWSER paints rather than us: scrollbars, form controls, spellcheck
  // underlines. It ships in the mode blocks, so a seed whose background sits on
  // the other side of the polarity line — a near-black page chosen while the
  // document is in light mode — resolves every color correctly by APCA and then
  // wears light scrollbars. Derived from the fill, so it cannot disagree.
  if (seed) {
    target.style.setProperty(
      "color-scheme",
      contrastInkFor(seed.background) === "#ffffff" ? "dark" : "light",
    );
  } else {
    target.style.removeProperty("color-scheme");
  }
}
